import { z } from "zod"
import { clientToolContent, type ClientToolContext } from "../nest/clientTools"
import { registerClientTool } from "../nest/client"
import type { JsonValue } from "../nest/generated"
import { BROWSER_TOOL_NAMES } from "../product"
import { showBrowserControlPrompt, type BrowserControlPromptPage, type BrowserControlPromptTarget } from "../components/browser/BrowserControlPromptModal"
import { showBrowserStartPrompt } from "../components/browser/BrowserStartPromptModal"
import {
    getEmbeddedBrowserStatus,
    importEmbeddedBrowserCookies,
    listEmbeddedBrowserCookieProfiles,
    type EmbeddedBrowserCookieImportResult,
} from "../electronAPIs/browser"
import { embeddedBrowserAvailabilityStore } from "./availability"
import { browserGrantDecision, browserStartRequiresConsent, type BrowserOperation } from "./permission"
import { markBrowserCookieImportOffered, shouldOfferBrowserCookieImport } from "./startConsent"
import { embeddedBrowserStore } from "./store"
import { embeddedBrowserSurfaceStore } from "./surfaceStore"
import {
    browserCoordinateTargetStillCurrent,
    browserScreenshotDigest,
    browserScreenshotPointToViewport,
    browserScreenshotStillCurrent,
    type BrowserCoordinateTarget,
    type BrowserPageMetrics,
} from "./observation"
import {
    extractContentScript,
    findInPageScript,
    highImpactControlName,
    inspectTypeSubmissionScript,
    prepareClickScript,
    prepareCoordinateClickScript,
    prepareDragScript,
    prepareHoverScript,
    prepareKeypressScript,
    prepareTypeScript,
    readPageScript,
    screenshotMetricsScript,
    scrollPageScript,
    selectOptionScript,
    type BrowserElementFingerprint,
} from "./scripts"
import type { EmbeddedBrowserTab } from "./tab"

const tabId = z.string().uuid().optional()
const userGrant = z
    .object({
        basis: z.enum(["explicit-user-request", "task-required"]),
        reason: z.string().min(1).max(500),
    })
    .optional()
const startSchema = z.object({
    reason: z.string().min(1).max(1_000).optional(),
})
const requestUserActionSchema = z.object({
    instruction: z.string().min(1).max(2_000),
    tabId,
})
const tabsSchema = z.object({
    action: z.enum(["list", "open", "select", "close"]),
    tabId,
    url: z.string().max(4_000).optional(),
    newTab: z.boolean().optional(),
})
const navigateSchema = z.object({
    action: z.enum(["goto", "back", "forward", "reload", "stop"]),
    tabId,
    url: z.string().max(4_000).optional(),
})
const readPageSchema = z.object({
    tabId,
    filter: z.enum(["interactive", "all"]).optional(),
    scopeRef: z.string().max(100).optional(),
    maxChars: z.number().int().min(1_000).max(50_000).optional(),
})
const extractSchema = z.object({
    tabId,
    maxChars: z.number().int().min(1_000).max(50_000).optional(),
})
const findSchema = z.object({
    tabId,
    query: z.string().min(1).max(500),
    maxResults: z.number().int().min(1).max(50).optional(),
})
const screenshotSchema = z.object({
    tabId,
    maxWidth: z.number().int().min(320).max(1_600).optional(),
})
const clickSchema = z
    .object({
        tabId,
        ref: z.string().max(100).optional(),
        screenshotId: z.string().uuid().optional(),
        x: z.number().finite().nonnegative().max(10_000).optional(),
        y: z.number().finite().nonnegative().max(10_000).optional(),
        userGrant,
    })
    .superRefine((value, context) => {
        const refTarget = value.ref !== undefined
        const pointTarget = value.screenshotId !== undefined && value.x !== undefined && value.y !== undefined
        if (refTarget === pointTarget)
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Provide either ref or screenshotId/x/y",
            })
    })
const refSchema = z.object({ tabId, ref: z.string().max(100) })
const typeSchema = refSchema.extend({
    text: z.string().max(4_000),
    mode: z.enum(["replace", "append"]).optional(),
    submit: z.boolean().optional(),
    userGrant,
})
const selectSchema = refSchema.extend({
    value: z.string().max(1_000),
    userGrant,
})
const scrollSchema = z.object({
    tabId,
    ref: z.string().max(100).optional(),
    direction: z.enum(["up", "down"]),
    amount: z.enum(["half-page", "page"]).optional(),
})
const pressSchema = z.object({
    tabId,
    key: z.enum(["Enter", "Escape", "Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"]),
    userGrant,
})
const dragSchema = z.object({
    tabId,
    startRef: z.string().max(100),
    endRef: z.string().max(100),
    userGrant,
})

const batchToolNames = [
    BROWSER_TOOL_NAMES.tabs,
    BROWSER_TOOL_NAMES.navigate,
    BROWSER_TOOL_NAMES.readPage,
    BROWSER_TOOL_NAMES.extractContent,
    BROWSER_TOOL_NAMES.find,
    BROWSER_TOOL_NAMES.click,
    BROWSER_TOOL_NAMES.hover,
    BROWSER_TOOL_NAMES.type,
    BROWSER_TOOL_NAMES.select,
    BROWSER_TOOL_NAMES.scroll,
    BROWSER_TOOL_NAMES.press,
    BROWSER_TOOL_NAMES.drag,
] as const

const batchSchema = z.object({
    calls: z
        .array(
            z.object({
                tool: z.enum(batchToolNames),
                arguments: z.record(z.unknown()),
            })
        )
        .min(1)
        .max(12),
    stopOnError: z.boolean().optional(),
})

type TabsInput = z.output<typeof tabsSchema>
type StartInput = z.output<typeof startSchema>
type RequestUserActionInput = z.output<typeof requestUserActionSchema>
type NavigateInput = z.output<typeof navigateSchema>
type ReadPageInput = z.output<typeof readPageSchema>
type ExtractInput = z.output<typeof extractSchema>
type FindInput = z.output<typeof findSchema>
type ScreenshotInput = z.output<typeof screenshotSchema>
type ClickInput = z.output<typeof clickSchema>
type RefInput = z.output<typeof refSchema>
type TypeInput = z.output<typeof typeSchema>
type SelectInput = z.output<typeof selectSchema>
type ScrollInput = z.output<typeof scrollSchema>
type PressInput = z.output<typeof pressSchema>
type DragInput = z.output<typeof dragSchema>
type BatchToolName = (typeof batchToolNames)[number]
type UserGrant = NonNullable<ClickInput["userGrant"]>

type BrowserControlPromptSnapshot = BrowserControlPromptPage & {
    tabId: string
    navigationGeneration: number
}

const batchControlToolNames: ReadonlySet<string> = new Set([
    BROWSER_TOOL_NAMES.click,
    BROWSER_TOOL_NAMES.type,
    BROWSER_TOOL_NAMES.select,
    BROWSER_TOOL_NAMES.press,
    BROWSER_TOOL_NAMES.drag,
])

type PreparedPoint = BrowserCoordinateTarget & { ok: true }
type PreparedDrag = {
    ok: true
    startX: number
    startY: number
    endX: number
    endY: number
    url: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function jsonValue(value: unknown): JsonValue {
    if (value === null || typeof value === "string" || typeof value === "boolean") return value
    if (typeof value === "number") {
        if (!Number.isFinite(value)) throw new Error("browser_invalid_result")
        return value
    }
    if (Array.isArray(value)) return value.map(jsonValue)
    if (isRecord(value)) {
        const result: Record<string, JsonValue> = {}
        for (const [key, item] of Object.entries(value)) {
            if (item !== undefined) result[key] = jsonValue(item)
        }
        return result
    }
    throw new Error("browser_invalid_result")
}

function controlPromptInputs(input: unknown): Record<string, unknown>[] {
    if (!isRecord(input)) return []
    if (!Array.isArray(input.calls)) return [input]
    return input.calls.flatMap((call) => {
        if (!isRecord(call) || typeof call.tool !== "string" || !batchControlToolNames.has(call.tool) || !isRecord(call.arguments)) return []
        return [call.arguments]
    })
}

function controlPromptContext(input: unknown, context: ClientToolContext): { pages: BrowserControlPromptSnapshot[]; targets: BrowserControlPromptTarget[] } {
    if (isRecord(input) && Array.isArray(input.calls)) {
        let targetContextChanged = false
        let controlCount = 0
        for (const call of input.calls) {
            if (!isRecord(call) || typeof call.tool !== "string" || !isRecord(call.arguments)) continue
            if (batchControlToolNames.has(call.tool)) {
                if (targetContextChanged || controlCount > 0) throw new Error("browser_batch_control_prompt_split_required")
                controlCount += 1
            }
            if (call.tool === BROWSER_TOOL_NAMES.navigate || (call.tool === BROWSER_TOOL_NAMES.tabs && call.arguments.action !== "list")) {
                targetContextChanged = true
            }
        }
    }
    const inputs = controlPromptInputs(input)
    const tabIds = new Set<string>()
    let usesSelectedTab = inputs.length === 0
    for (const candidate of inputs) {
        if (typeof candidate.tabId === "string") tabIds.add(candidate.tabId)
        else usesSelectedTab = true
    }
    if (usesSelectedTab) tabIds.add(embeddedBrowserStore.requireAgentTab(context).id)

    const pages = [...tabIds].map((tabId) => {
        const tab = embeddedBrowserStore.requireAgentTab(context, tabId)
        return {
            tabId: tab.id,
            title: tab.title,
            url: tab.url,
            navigationGeneration: tab.navigationGeneration,
        }
    })
    const targets: BrowserControlPromptTarget[] = []
    for (const candidate of inputs) {
        const tab = embeddedBrowserStore.requireAgentTab(context, typeof candidate.tabId === "string" ? candidate.tabId : undefined)
        for (const key of ["ref", "startRef", "endRef"] as const) {
            if (typeof candidate[key] !== "string") continue
            try {
                const fingerprint = embeddedBrowserStore.fingerprint(tab.id, candidate[key])
                targets.push({ tabId: tab.id, ...fingerprint })
            } catch {
                // The executor will report a stale ref after the user decision;
                // the approval dialog should still show the exact raw payload.
            }
        }
    }
    return { pages, targets }
}

function revalidateControlPromptContext(context: ClientToolContext, pages: BrowserControlPromptSnapshot[]): void {
    for (const expected of pages) {
        const tab = embeddedBrowserStore.requireAgentTab(context, expected.tabId)
        if (tab.navigationGeneration !== expected.navigationGeneration || tab.url !== expected.url) {
            throw new Error("browser_control_context_changed")
        }
    }
}

function parseFingerprint(value: unknown): BrowserElementFingerprint | null {
    if (
        !isRecord(value) ||
        typeof value.tag !== "string" ||
        typeof value.role !== "string" ||
        typeof value.type !== "string" ||
        typeof value.name !== "string" ||
        typeof value.actionable !== "boolean" ||
        typeof value.scrollable !== "boolean"
    ) {
        return null
    }
    return {
        tag: value.tag,
        role: value.role,
        type: value.type,
        name: value.name,
        actionable: value.actionable,
        scrollable: value.scrollable,
    }
}

function preparedPoint(value: unknown): PreparedPoint {
    if (!isRecord(value) || value.ok !== true || typeof value.x !== "number" || typeof value.y !== "number" || typeof value.url !== "string") {
        throw new Error(isRecord(value) && typeof value.code === "string" ? `browser_${value.code}` : "browser_action_denied")
    }
    const fingerprint = parseFingerprint(value.fingerprint)
    return {
        ok: true,
        x: value.x,
        y: value.y,
        url: value.url,
        ...(typeof value.submit === "boolean" ? { submit: value.submit } : {}),
        ...(value.submissionIntent === "search" || value.submissionIntent === "authentication" || value.submissionIntent === "other"
            ? { submissionIntent: value.submissionIntent }
            : {}),
        ...(typeof value.opaque === "boolean" ? { opaque: value.opaque } : {}),
        ...(fingerprint ? { fingerprint } : {}),
    }
}

function preparedDrag(value: unknown): PreparedDrag {
    if (
        !isRecord(value) ||
        value.ok !== true ||
        typeof value.startX !== "number" ||
        typeof value.startY !== "number" ||
        typeof value.endX !== "number" ||
        typeof value.endY !== "number" ||
        typeof value.url !== "string"
    ) {
        throw new Error(isRecord(value) && typeof value.code === "string" ? `browser_${value.code}` : "browser_action_denied")
    }
    return {
        ok: true,
        startX: value.startX,
        startY: value.startY,
        endX: value.endX,
        endY: value.endY,
        url: value.url,
    }
}

function parseMetrics(value: unknown): BrowserPageMetrics {
    if (!isRecord(value) || typeof value.url !== "string" || !isRecord(value.viewport)) throw new Error("browser_invalid_result")
    const viewport = value.viewport
    if (
        typeof viewport.width !== "number" ||
        typeof viewport.height !== "number" ||
        typeof viewport.scrollX !== "number" ||
        typeof viewport.scrollY !== "number" ||
        typeof viewport.devicePixelRatio !== "number"
    ) {
        throw new Error("browser_invalid_result")
    }
    return {
        url: value.url,
        viewport: {
            width: viewport.width,
            height: viewport.height,
            scrollX: viewport.scrollX,
            scrollY: viewport.scrollY,
            devicePixelRatio: viewport.devicePixelRatio,
        },
    }
}

function tabSummary(tab: EmbeddedBrowserTab, context: ClientToolContext): JsonValue {
    return {
        tabId: tab.id,
        title: tab.title,
        url: tab.url,
        active: tab.id === embeddedBrowserStore.activeAgentTabId(context),
        loading: tab.loading,
        canGoBack: tab.canGoBack,
        canGoForward: tab.canGoForward,
    }
}

function cookieImportSummary(result: EmbeddedBrowserCookieImportResult | null): JsonValue {
    if (!result) {
        return {
            requested: 1,
            succeeded: 0,
            failed: 1,
            state: "done-with-gaps",
            firstErrors: ["browser_cookie_import_failed"],
        }
    }
    return {
        requested: result.requested,
        succeeded: result.imported,
        failed: result.skipped,
        warnings: result.warnings,
        state: result.skipped > 0 || result.partial || !result.retiredPartitionCleared ? "done-with-gaps" : "completed",
        firstErrors: [...result.firstErrors, ...(!result.retiredPartitionCleared ? ["browser_retired_partition_clear_failed"] : [])],
    }
}

const approvedBrowserSessionIds = new Set<string>()
let browserStartQueue: Promise<void> = Promise.resolve()

function throwIfClientToolCancelled(context: ClientToolContext): void {
    if (context.signal.aborted) throw new Error("browser_action_cancelled")
}

function waitForClientTool<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
    if (signal.aborted) return Promise.reject(new Error("browser_action_cancelled"))
    return new Promise<T>((resolve, reject) => {
        const abort = () => reject(new Error("browser_action_cancelled"))
        signal.addEventListener("abort", abort, { once: true })
        void promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort))
    })
}

async function performBrowserStart(input: StartInput, context: ClientToolContext): Promise<JsonValue> {
    throwIfClientToolCancelled(context)
    if (!embeddedBrowserSurfaceStore.isRunning) approvedBrowserSessionIds.clear()
    const reused = embeddedBrowserSurfaceStore.isRunning
    let importResult: EmbeddedBrowserCookieImportResult | null | undefined

    if (browserStartRequiresConsent(context.permissionMode) && !approvedBrowserSessionIds.has(context.sessionId)) {
        let cookieProfiles: Awaited<ReturnType<typeof listEmbeddedBrowserCookieProfiles>> = []
        if (!embeddedBrowserSurfaceStore.isRunning) {
            const status = await getEmbeddedBrowserStatus()
            throwIfClientToolCancelled(context)
            if (!status) throw new Error("browser_unavailable")
            const offerCookieImport = status.features.cookieImport && shouldOfferBrowserCookieImport()
            cookieProfiles = offerCookieImport ? ((await listEmbeddedBrowserCookieProfiles()) ?? []) : []
        }
        throwIfClientToolCancelled(context)
        const response = await showBrowserStartPrompt({ reason: input.reason, cookieProfiles: cookieProfiles ?? [] }, context.signal)
        throwIfClientToolCancelled(context)
        if ((cookieProfiles?.length ?? 0) > 0) markBrowserCookieImportOffered()
        if (response.decision !== "allow") throw new Error("browser_permission_denied")
        approvedBrowserSessionIds.add(context.sessionId)
        if (response.importProfileId) importResult = await importEmbeddedBrowserCookies(response.importProfileId)
    }

    throwIfClientToolCancelled(context)
    if (!embeddedBrowserSurfaceStore.isRunning) {
        try {
            embeddedBrowserSurfaceStore.startHidden()
            embeddedBrowserStore.resumeGlobalAgentControl()
            embeddedBrowserStore.setNotice(null)
            await embeddedBrowserStore.initialize()
        } catch (error) {
            embeddedBrowserStore.stopSession()
            throw error
        }
    }
    const tab = embeddedBrowserStore.ensureAgentTab(context)
    await waitForClientTool(embeddedBrowserStore.surface(tab.id), context.signal)
    approvedBrowserSessionIds.add(context.sessionId)
    return jsonValue({
        started: true,
        reused,
        visible: reused ? embeddedBrowserSurfaceStore.isVisible : false,
        ...(importResult !== undefined ? { cookieImport: cookieImportSummary(importResult) } : {}),
        tabId: tab.id,
        agentPaused: embeddedBrowserStore.isAgentPaused(context),
    })
}

function startBrowser(input: StartInput, context: ClientToolContext): Promise<JsonValue> {
    const started = browserStartQueue.then(() => performBrowserStart(input, context))
    browserStartQueue = started.then(
        () => undefined,
        () => undefined
    )
    return waitForClientTool(started, context.signal)
}

async function requestUserAction(input: RequestUserActionInput, context: ClientToolContext): Promise<JsonValue> {
    return embeddedBrowserStore.requestUserAction(context, input.instruction, input.tabId)
}

async function tabs(input: TabsInput, context: ClientToolContext, initiatedByAgent = false): Promise<JsonValue> {
    await embeddedBrowserStore.initialize()
    if (input.action === "list") {
        const ownedTabs = embeddedBrowserStore.agentTabs(context)
        return {
            tabs: ownedTabs.map((tab) => tabSummary(tab, context)),
            activeTabId: embeddedBrowserStore.activeAgentTabId(context),
            otherTabCount: embeddedBrowserStore.tabs.length - ownedTabs.length,
        }
    }
    if (input.action === "select") return tabSummary(embeddedBrowserStore.selectAgentTab(context, input.tabId ?? ""), context)
    if (input.action === "close") {
        const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
        embeddedBrowserStore.closeTab(tab.id, initiatedByAgent)
        return {
            closed: true,
            tabId: tab.id,
            activeTabId: embeddedBrowserStore.activeAgentTabId(context),
        }
    }
    const url = input.url ?? "about:blank"
    if (input.newTab !== false) {
        const tab = embeddedBrowserStore.createAgentTab(context, url)
        await embeddedBrowserStore.surface(tab.id)
        return tabSummary(tab, context)
    }
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    await embeddedBrowserStore.navigate(tab.id, url)
    return tabSummary(tab, context)
}

async function navigate(input: NavigateInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const surface = await embeddedBrowserStore.surface(tab.id)
    if (input.action === "goto") {
        if (!input.url) throw new Error("browser_url_required")
        await embeddedBrowserStore.navigate(tab.id, input.url)
    } else if (input.action === "back") {
        if (!tab.canGoBack) throw new Error("browser_history_unavailable")
        const generation = tab.navigationGeneration
        surface.goBack()
        await embeddedBrowserStore.waitForNavigation(tab.id, generation, true)
    } else if (input.action === "forward") {
        if (!tab.canGoForward) throw new Error("browser_history_unavailable")
        const generation = tab.navigationGeneration
        surface.goForward()
        await embeddedBrowserStore.waitForNavigation(tab.id, generation, true)
    } else if (input.action === "reload") surface.reload()
    else surface.stop()
    return tabSummary(tab, context)
}

async function readPage(input: ReadPageInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const surface = await embeddedBrowserStore.surface(tab.id)
    if (input.scopeRef) embeddedBrowserStore.fingerprint(tab.id, input.scopeRef)
    const generation = tab.beginSnapshot()
    const raw = await surface.executeJavaScript(
        readPageScript({
            generation,
            filter: input.filter ?? "all",
            maxChars: input.maxChars ?? 30_000,
            ...(input.scopeRef ? { scopeRef: input.scopeRef } : {}),
        })
    )
    if (!isRecord(raw) || raw.ok !== true || typeof raw.title !== "string" || typeof raw.url !== "string" || !Array.isArray(raw.elements)) {
        throw new Error(isRecord(raw) && typeof raw.code === "string" ? `browser_${raw.code}` : "browser_invalid_result")
    }
    const fingerprints = new Map<string, BrowserElementFingerprint>()
    if (isRecord(raw.fingerprints)) {
        for (const [ref, value] of Object.entries(raw.fingerprints)) {
            const fingerprint = parseFingerprint(value)
            if (fingerprint) fingerprints.set(ref, fingerprint)
        }
    }
    embeddedBrowserStore.setFingerprints(tab.id, fingerprints)
    return jsonValue({
        tabId: tab.id,
        title: raw.title,
        url: raw.url,
        text: typeof raw.text === "string" ? raw.text : "",
        headings: Array.isArray(raw.headings) ? raw.headings : [],
        viewport: raw.viewport,
        elements: raw.elements,
    })
}

async function extractContent(input: ExtractInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const result = await surface.executeJavaScript(extractContentScript(input.maxChars ?? 50_000))
    if (!isRecord(result)) throw new Error("browser_invalid_result")
    return jsonValue({ tabId: tab.id, ...result })
}

async function find(input: FindInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const surface = await embeddedBrowserStore.surface(tab.id)
    return jsonValue({
        tabId: tab.id,
        result: await surface.executeJavaScript(findInPageScript(input.query, input.maxResults ?? 20)),
    })
}

async function screenshot(input: ScreenshotInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const page = parseMetrics(await surface.executeJavaScript(screenshotMetricsScript()))
    const source = await surface.capturePage()
    const sourceImage = source.getSize()
    const maxWidth = input.maxWidth ?? 1_400
    const emitted = sourceImage.width > maxWidth ? source.resize({ width: maxWidth, quality: "better" }) : source
    const emittedImage = emitted.getSize()
    const sourceDataUrl = source.toDataURL()
    const emittedDataUrl = emitted.toDataURL()
    const id = crypto.randomUUID()
    embeddedBrowserStore.rememberScreenshot({
        id,
        tabId: tab.id,
        navigationGeneration: tab.navigationGeneration,
        page,
        sourceImage,
        emittedImage,
        sourceDigest: await browserScreenshotDigest(sourceDataUrl),
    })
    const comma = emittedDataUrl.indexOf(",")
    if (comma < 0) throw new Error("browser_invalid_screenshot")
    const metadata: JsonValue = {
        screenshotId: id,
        tabId: tab.id,
        title: tab.title,
        url: tab.url,
        width: emittedImage.width,
        height: emittedImage.height,
    }
    return clientToolContent(
        [
            { type: "text", text: JSON.stringify(metadata) },
            {
                type: "image",
                data: emittedDataUrl.slice(comma + 1),
                mimeType: "image/png",
            },
        ],
        metadata
    )
}

function assertLowImpact(fingerprint: BrowserElementFingerprint | undefined, authorized: boolean): void {
    if (fingerprint && highImpactControlName(fingerprint.name) && !authorized) throw new Error("browser_confirmation_required")
}

function assertSafeSubmission(
    submit: boolean | undefined,
    fingerprint: BrowserElementFingerprint | undefined,
    authorized: boolean,
    submissionIntent?: BrowserCoordinateTarget["submissionIntent"]
): void {
    if (!submit) return
    if (submissionIntent === "search" || submissionIntent === "authentication") return
    if (fingerprint && (fingerprint.type === "search" || /\b(search|find)\b/i.test(fingerprint.name))) return
    if (!authorized) throw new Error("browser_confirmation_required")
}

function actionAuthorized(context: ClientToolContext, grant?: UserGrant): boolean {
    return context.permissionMode === "ask" || grant !== undefined
}

function mouseClick(surface: Awaited<ReturnType<typeof embeddedBrowserStore.surface>>, point: { x: number; y: number }): void {
    surface.sendInputEvent({ type: "mouseMove", x: point.x, y: point.y })
    surface.sendInputEvent({
        type: "mouseDown",
        x: point.x,
        y: point.y,
        button: "left",
        clickCount: 1,
    })
    surface.sendInputEvent({
        type: "mouseUp",
        x: point.x,
        y: point.y,
        button: "left",
        clickCount: 1,
    })
}

async function coordinateClick(input: ClickInput, tab: EmbeddedBrowserTab, authorized: boolean): Promise<JsonValue> {
    if (!input.screenshotId || input.x === undefined || input.y === undefined) throw new Error("browser_point_required")
    const observation = embeddedBrowserStore.screenshot(input.screenshotId)
    if (observation.tabId !== tab.id) throw new Error("browser_stale_screenshot")
    const surface = await embeddedBrowserStore.surface(tab.id)
    const page = parseMetrics(await surface.executeJavaScript(screenshotMetricsScript()))
    const freshImage = await surface.capturePage()
    const freshDigest = await browserScreenshotDigest(freshImage.toDataURL())
    if (!browserScreenshotStillCurrent(observation, tab.navigationGeneration, page, freshDigest)) throw new Error("browser_stale_screenshot")
    const viewportPoint = browserScreenshotPointToViewport(observation, {
        x: input.x,
        y: input.y,
    })
    if (!viewportPoint) throw new Error("browser_point_outside_screenshot")
    const before = preparedPoint(await surface.executeJavaScript(prepareCoordinateClickScript(viewportPoint.x, viewportPoint.y)))
    if (before.opaque) throw new Error("browser_confirmation_required")
    assertLowImpact(before.fingerprint, authorized)
    assertSafeSubmission(before.submit, before.fingerprint, authorized, before.submissionIntent)
    const after = preparedPoint(await surface.executeJavaScript(prepareCoordinateClickScript(viewportPoint.x, viewportPoint.y)))
    if (!browserCoordinateTargetStillCurrent(before, after)) throw new Error("browser_stale_screenshot")
    const generation = tab.navigationGeneration
    mouseClick(surface, after)
    await embeddedBrowserStore.waitForNavigation(tab.id, generation)
    return jsonValue({ ok: true, tabId: tab.id, url: tab.url, target: after })
}

async function click(input: ClickInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const authorized = actionAuthorized(context, input.userGrant)
    if (!input.ref) return coordinateClick(input, tab, authorized)
    const fingerprint = embeddedBrowserStore.fingerprint(tab.id, input.ref)
    assertLowImpact(fingerprint, authorized)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const point = preparedPoint(await surface.executeJavaScript(prepareClickScript(input.ref, fingerprint)))
    assertSafeSubmission(point.submit, point.fingerprint, authorized, point.submissionIntent)
    const generation = tab.navigationGeneration
    mouseClick(surface, point)
    await embeddedBrowserStore.waitForNavigation(tab.id, generation)
    return jsonValue({ ok: true, tabId: tab.id, ref: input.ref, url: tab.url })
}

async function hover(input: RefInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const fingerprint = embeddedBrowserStore.fingerprint(tab.id, input.ref)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const point = preparedPoint(await surface.executeJavaScript(prepareHoverScript(input.ref, fingerprint)))
    surface.sendInputEvent({ type: "mouseMove", x: point.x, y: point.y })
    return jsonValue({ ok: true, tabId: tab.id, ref: input.ref, url: point.url })
}

async function typeText(input: TypeInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const fingerprint = embeddedBrowserStore.fingerprint(tab.id, input.ref)
    const authorized = actionAuthorized(context, input.userGrant)
    const surface = await embeddedBrowserStore.surface(tab.id)
    let expectedSubmissionIntent: BrowserCoordinateTarget["submissionIntent"]
    if (input.submit) {
        const inspected = await surface.executeJavaScript(inspectTypeSubmissionScript(input.ref, fingerprint))
        if (
            !isRecord(inspected) ||
            inspected.ok !== true ||
            (inspected.submissionIntent !== "search" && inspected.submissionIntent !== "authentication" && inspected.submissionIntent !== "other")
        ) {
            throw new Error(isRecord(inspected) && typeof inspected.code === "string" ? `browser_${inspected.code}` : "browser_action_denied")
        }
        expectedSubmissionIntent = inspected.submissionIntent
        assertLowImpact(fingerprint, authorized)
        assertSafeSubmission(true, fingerprint, authorized, expectedSubmissionIntent)
    }
    const prepared = await surface.executeJavaScript(prepareTypeScript(input.ref, fingerprint, input.mode ?? "replace", expectedSubmissionIntent))
    if (!isRecord(prepared) || prepared.ok !== true) {
        throw new Error(isRecord(prepared) && typeof prepared.code === "string" ? `browser_${prepared.code}` : "browser_action_denied")
    }
    surface.insertText(input.text)
    if (input.submit) {
        const generation = tab.navigationGeneration
        surface.sendInputEvent({ type: "keyDown", keyCode: "Enter" })
        surface.sendInputEvent({ type: "keyUp", keyCode: "Enter" })
        await embeddedBrowserStore.waitForNavigation(tab.id, generation)
    }
    return {
        ok: true,
        tabId: tab.id,
        ref: input.ref,
        submitted: input.submit === true,
        url: tab.url,
    }
}

async function select(input: SelectInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const fingerprint = embeddedBrowserStore.fingerprint(tab.id, input.ref)
    const authorized = actionAuthorized(context, input.userGrant)
    assertLowImpact(fingerprint, authorized)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const prepared = await surface.executeJavaScript(selectOptionScript(input.ref, fingerprint, input.value))
    if (!isRecord(prepared) || prepared.ok !== true || typeof prepared.value !== "string" || typeof prepared.label !== "string") {
        throw new Error(isRecord(prepared) && typeof prepared.code === "string" ? `browser_${prepared.code}` : "browser_action_denied")
    }
    const optionName = `${prepared.label} ${prepared.value.replace(/[_-]+/g, " ")}`
    if ((highImpactControlName(optionName) || /\b(public|private|administrator|owner)\b/i.test(optionName)) && !authorized) {
        throw new Error("browser_confirmation_required")
    }
    const result = await surface.executeJavaScript(selectOptionScript(input.ref, fingerprint, input.value, { value: prepared.value, label: prepared.label }))
    if (!isRecord(result) || result.ok !== true) {
        throw new Error(isRecord(result) && typeof result.code === "string" ? `browser_${result.code}` : "browser_action_denied")
    }
    return jsonValue({ tabId: tab.id, ...result })
}

async function scroll(input: ScrollInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const fingerprint = input.ref ? embeddedBrowserStore.fingerprint(tab.id, input.ref) : undefined
    const surface = await embeddedBrowserStore.surface(tab.id)
    const result = await surface.executeJavaScript(scrollPageScript(input.direction, input.amount ?? "page", input.ref, fingerprint))
    if (isRecord(result) && result.ok === false) throw new Error(typeof result.code === "string" ? `browser_${result.code}` : "browser_action_denied")
    return jsonValue({ tabId: tab.id, result })
}

async function press(input: PressInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const prepared = await surface.executeJavaScript(prepareKeypressScript(input.key))
    if (!isRecord(prepared) || prepared.ok !== true) throw new Error("browser_action_denied")
    if (
        prepared.submit === true &&
        prepared.submissionIntent !== "search" &&
        prepared.submissionIntent !== "authentication" &&
        !actionAuthorized(context, input.userGrant)
    )
        throw new Error("browser_confirmation_required")
    surface.sendInputEvent({ type: "keyDown", keyCode: input.key })
    surface.sendInputEvent({ type: "keyUp", keyCode: input.key })
    return { ok: true, tabId: tab.id, key: input.key, url: tab.url }
}

async function drag(input: DragInput, context: ClientToolContext): Promise<JsonValue> {
    const tab = embeddedBrowserStore.requireAgentTab(context, input.tabId)
    const startFingerprint = embeddedBrowserStore.fingerprint(tab.id, input.startRef)
    const endFingerprint = embeddedBrowserStore.fingerprint(tab.id, input.endRef)
    const authorized = actionAuthorized(context, input.userGrant)
    assertLowImpact(startFingerprint, authorized)
    assertLowImpact(endFingerprint, authorized)
    const surface = await embeddedBrowserStore.surface(tab.id)
    const target = preparedDrag(await surface.executeJavaScript(prepareDragScript(input.startRef, startFingerprint, input.endRef, endFingerprint)))
    surface.sendInputEvent({
        type: "mouseMove",
        x: target.startX,
        y: target.startY,
    })
    surface.sendInputEvent({
        type: "mouseDown",
        x: target.startX,
        y: target.startY,
        button: "left",
        clickCount: 1,
    })
    for (let step = 1; step <= 6; step += 1) {
        const progress = step / 6
        surface.sendInputEvent({
            type: "mouseMove",
            x: Math.round(target.startX + (target.endX - target.startX) * progress),
            y: Math.round(target.startY + (target.endY - target.startY) * progress),
        })
    }
    surface.sendInputEvent({
        type: "mouseUp",
        x: target.endX,
        y: target.endY,
        button: "left",
        clickCount: 1,
    })
    return {
        ok: true,
        tabId: tab.id,
        startRef: input.startRef,
        endRef: input.endRef,
        url: target.url,
    }
}

const batchSchemas: Record<BatchToolName, z.ZodTypeAny> = {
    [BROWSER_TOOL_NAMES.tabs]: tabsSchema,
    [BROWSER_TOOL_NAMES.navigate]: navigateSchema,
    [BROWSER_TOOL_NAMES.readPage]: readPageSchema,
    [BROWSER_TOOL_NAMES.extractContent]: extractSchema,
    [BROWSER_TOOL_NAMES.find]: findSchema,
    [BROWSER_TOOL_NAMES.click]: clickSchema,
    [BROWSER_TOOL_NAMES.hover]: refSchema,
    [BROWSER_TOOL_NAMES.type]: typeSchema,
    [BROWSER_TOOL_NAMES.select]: selectSchema,
    [BROWSER_TOOL_NAMES.scroll]: scrollSchema,
    [BROWSER_TOOL_NAMES.press]: pressSchema,
    [BROWSER_TOOL_NAMES.drag]: dragSchema,
}

async function executeBatchCall(tool: BatchToolName, argumentsValue: Record<string, unknown>, context: ClientToolContext): Promise<JsonValue> {
    throwIfClientToolCancelled(context)
    const parsed: unknown = batchSchemas[tool].parse(argumentsValue)
    if (tool === BROWSER_TOOL_NAMES.tabs) return tabs(tabsSchema.parse(parsed), context, true)
    if (tool === BROWSER_TOOL_NAMES.navigate) return navigate(navigateSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.readPage) return readPage(readPageSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.extractContent) return extractContent(extractSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.find) return find(findSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.click) return click(clickSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.hover) return hover(refSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.type) return typeText(typeSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.select) return select(selectSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.scroll) return scroll(scrollSchema.parse(parsed), context)
    if (tool === BROWSER_TOOL_NAMES.press) return press(pressSchema.parse(parsed), context)
    return drag(dragSchema.parse(parsed), context)
}

export function installEmbeddedBrowserTools(): () => void {
    const registerAgentTool = <TSchema extends z.ZodTypeAny>(
        definition: { name: string; description: string; schema: TSchema },
        callback: (input: z.output<TSchema>, context: ClientToolContext) => Promise<JsonValue>,
        options: {
            allowWhilePaused?: boolean
            operation?: BrowserOperation | ((input: z.output<TSchema>) => BrowserOperation)
        } = {}
    ) =>
        registerClientTool(definition, (input, context) => {
            if (!embeddedBrowserAvailabilityStore.available) throw new Error("browser_unavailable")
            if (!embeddedBrowserSurfaceStore.isRunning) throw new Error("browser_start_required")
            if (!approvedBrowserSessionIds.has(context.sessionId)) throw new Error("browser_start_required")
            return embeddedBrowserStore.runAgentAction(
                definition.name,
                context,
                async () => {
                    const operation = typeof options.operation === "function" ? options.operation(input) : (options.operation ?? "observe")
                    const decision = browserGrantDecision(context, operation)
                    if (decision === "deny") throw new Error("browser_read_only")
                    if (decision === "prompt") {
                        const promptContext = controlPromptContext(input, context)
                        const response = await showBrowserControlPrompt(
                            {
                                toolName: definition.name,
                                pages: promptContext.pages,
                                targets: promptContext.targets,
                                input: jsonValue(input),
                            },
                            context.signal
                        )
                        if (response.decision !== "allow") throw new Error("browser_permission_denied")
                        revalidateControlPromptContext(context, promptContext.pages)
                    }
                    return callback(input, context)
                },
                options
            )
        })

    const disposers = [
        registerClientTool(
            {
                name: BROWSER_TOOL_NAMES.start,
                description:
                    "Start WildLark's isolated Browser hidden at a useful viewport size. Ask/auto request per-session Browser access; yolo uses the turn grant without another prompt. Call this before every other Browser tool. This assigns the calling agent session its own selected tab; other sessions keep separate tabs. The first eligible prompt can copy current Chrome or Edge website sessions.",
                schema: startSchema,
            },
            (input, context) => {
                if (!embeddedBrowserAvailabilityStore.available) throw new Error("browser_unavailable")
                return embeddedBrowserStore.runLifecycleAction(BROWSER_TOOL_NAMES.start, context, () => startBrowser(input, context))
            }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.requestUserAction,
                description:
                    "Ask the user to perform one concrete step on this agent session's selected or explicitly targeted Browser tab. Requests on separate tabs remain independent; Browser reveals the target unless the user is already handling another request. Use this for protected credential or verification input, or before a consequential external side effect that the user did not explicitly request and that is not plainly required by their requested outcome. A direct or clearly implied user request already grants that action: perform it with the control tool's userGrant instead of handing it back. This rule applies in every permission mode. The tool registers the handoff and returns immediately: end the current turn and wait without polling or retrying Browser tools. The user's Done or unable response, including any optional note about what they did or what the agent should do next, starts a new turn in this same session, and an undelivered response remains pending for retry. Other agent sessions may continue on their own tabs.",
                schema: requestUserActionSchema,
            },
            requestUserAction,
            { allowWhilePaused: true, operation: "user-action" }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.tabs,
                description:
                    "List, open, select, or close only the calling agent session's Browser tabs. Opening defaults to a new owned tab and selects it for this agent without changing the tab visible to the user; set newTab=false to reuse this agent's selected tab. Tab IDs owned by other sessions are rejected.",
                schema: tabsSchema,
            },
            (input, context) => tabs(input, context, true)
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.navigate,
                description:
                    "Navigate the selected Browser tab to an HTTP or HTTPS URL, or go back, forward, reload, or stop loading. Do not navigate directly to an action or confirmation URL that itself causes an external side effect; use a visible control so its authorization can be enforced.",
                schema: navigateSchema,
            },
            navigate
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.readPage,
                description:
                    "Read a fixed, model-friendly projection of the page. Returns text, headings, viewport data, and generation-scoped element refs. Use filter=interactive for controls only, or scopeRef to read within a previously returned element.",
                schema: readPageSchema,
            },
            readPage
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.extractContent,
                description: "Extract the main article/content text and metadata from the selected page, excluding navigation, forms, and page chrome.",
                schema: extractSchema,
            },
            extractContent
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.find,
                description: `Find visible text on the current page. Returns bounded excerpts and existing element refs when a prior ${BROWSER_TOOL_NAMES.readPage} assigned one.`,
                schema: findSchema,
            },
            find
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.screenshot,
                description:
                    "Capture this agent session's selected Browser-tab viewport, including when that tab is not visible or focused. Returns a real MCP image block plus a screenshotId that can be used for a freshness-checked coordinate click.",
                schema: screenshotSchema,
            },
            screenshot
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.click,
                description:
                    "Click either a fresh element ref or a point from a named screenshot. Ref clicks revalidate the element fingerprint and hit target; point clicks revalidate URL, viewport, pixels, and target immediately before native input. For a consequential external effect, include userGrant only when the user directly requested it or it is plainly required by their requested outcome; otherwise use wildlark_browser_request_user_action.",
                schema: clickSchema,
            },
            click,
            { operation: "control" }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.hover,
                description: "Hover a fresh element ref using native Chromium mouse input.",
                schema: refSchema,
            },
            hover
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.type,
                description:
                    "Type into a fresh non-sensitive text field ref using native insertion. Password, payment, file, and OTP fields are never exposed. Drafting is allowed. submit=true is allowed for search/find, or with userGrant when submission was directly requested or is plainly required by the user's requested outcome; otherwise use wildlark_browser_request_user_action.",
                schema: typeSchema,
            },
            typeText,
            { operation: "control" }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.select,
                description:
                    "Select an option in a fresh native select element ref by option value or visible label. If selection itself commits a consequential external change, include userGrant only for a directly requested or plainly required action; otherwise use wildlark_browser_request_user_action.",
                schema: selectSchema,
            },
            select,
            { operation: "control" }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.scroll,
                description: "Scroll the page or a fresh scrollable element ref up or down by half a page or a page.",
                schema: scrollSchema,
            },
            scroll
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.press,
                description:
                    "Send one supported navigation/editing key to the focused page target. For a submission or consequential external effect, include userGrant only when directly requested or plainly required by the user's requested outcome; otherwise use wildlark_browser_request_user_action.",
                schema: pressSchema,
            },
            press,
            { operation: "control" }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.drag,
                description:
                    "Drag from one fresh element ref to another using a short sequence of native mouse events. If dropping commits a consequential external change, include userGrant only for a directly requested or plainly required action; otherwise use wildlark_browser_request_user_action.",
                schema: dragSchema,
            },
            drag,
            { operation: "control" }
        ),
        registerAgentTool(
            {
                name: BROWSER_TOOL_NAMES.batch,
                description:
                    "Run up to 12 Browser calls sequentially within this agent session's owned tabs and stop on the first failure by default. Screenshot and nested batch calls are excluded. Consequential calls require their own valid userGrant; otherwise use wildlark_browser_request_user_action. Always reports requested/succeeded/failed counts and first errors.",
                schema: batchSchema,
            },
            async (input, context) => {
                const results: JsonValue[] = []
                const errors: JsonValue[] = []
                for (let index = 0; index < input.calls.length; index += 1) {
                    throwIfClientToolCancelled(context)
                    const call = input.calls[index]
                    if (!call) continue
                    try {
                        results.push({
                            index,
                            tool: call.tool,
                            result: await executeBatchCall(call.tool, call.arguments, context),
                        })
                    } catch (error) {
                        const message = error instanceof Error ? error.message : "browser_failed"
                        const manualAction = message === "browser_sensitive_control"
                        if (manualAction) embeddedBrowserStore.requireManualAction(context)
                        errors.push({ index, tool: call.tool, error: message })
                        if (input.stopOnError !== false || manualAction) break
                    }
                }
                return {
                    requested: input.calls.length,
                    succeeded: results.length,
                    failed: errors.length,
                    skipped: input.calls.length - results.length - errors.length,
                    state: errors.length > 0 ? "done-with-gaps" : "completed",
                    results,
                    firstErrors: errors.slice(0, 3),
                }
            },
            {
                operation: (input) => (input.calls.some((call) => batchControlToolNames.has(call.tool)) ? "control" : "observe"),
            }
        ),
    ]
    return () => disposers.forEach((dispose) => dispose())
}
